import axios from 'axios';

const normalizeBaseUrl = (value) => {
    if (!value) return '/api/v1';
    const trimmed = value.trim();
    if (!trimmed) return '/api/v1';

    if (trimmed.startsWith('//')) {
        if (typeof window !== 'undefined') {
            return `${window.location.protocol}${trimmed}`.replace(/\/$/, '');
        }
        return `https:${trimmed}`.replace(/\/$/, '');
    }

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        try {
            const url = new URL(trimmed);
            if (url.protocol !== 'http:' && url.protocol !== 'https:') {
                return '/api/v1';
            }
            if (typeof window !== 'undefined' && window.location.protocol === 'https:' && url.protocol === 'http:') {
                url.protocol = 'https:';
            }
            return url.toString().replace(/\/$/, '');
        } catch {
            return trimmed.replace(/\/$/, '');
        }
    }

    return trimmed.replace(/\/$/, '');
};

// export const getApiBaseUrl = () => normalizeBaseUrl(import.meta.env.VITE_API_URL || '/api');
export const getApiBaseUrl = () => {
    // If we're in a browser environment, detect URL dynamically
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        // 1. Production Domain
        if (hostname === 'dev.rptacclr.metlife.com') {
            return import.meta.env.VITE_PROD_API_URL || 'https://dev.rptacclr.api.metlife.com/api/v1';
        }

        // 3. Dev IP (e.g. 10.133.81.168)
        if (hostname === '10.133.81.168') {
            return import.meta.env.VITE_DEV_API_URL || 'http://10.133.81.168:8000/api/v1';
        }
    }
    // Fallback to Env var or relative path
    return normalizeBaseUrl(import.meta.env.VITE_API_URL || '/api/v1');
};



export const getAppBasePath = () => {
    const raw = import.meta.env.VITE_BASENAME || '/';
    const trimmed = String(raw).trim() || '/';
    const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
};

export const resolveAppPath = (path = '') => {
    const basePath = getAppBasePath();
    const cleanPath = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
    return `${basePath}${cleanPath}`.replace(/\/{2,}/g, '/').replace(':/', '://');
};

// Create axios instance with base configuration
const api = axios.create({
    baseURL: getApiBaseUrl(),
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

let authTokenProvider = null;

export const setAuthTokenProvider = (provider) => {
    authTokenProvider = provider;
};

api.interceptors.request.use(async (config) => {
    if (authTokenProvider) {
        const token = await authTokenProvider();
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
    }
    return config;
});

const parseDownloadFilename = (contentDisposition, fallback) => {
    const value = String(contentDisposition || '').trim();
    if (!value) return fallback;

    const utf8Match = value.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1]).replace(/^["']/, '').replace(/["']$/, '') || fallback;
        } catch {
            return utf8Match[1].replace(/^["']/, '').replace(/["']$/, '') || fallback;
        }
    }

    const basicMatch = value.match(/filename\s*=\s*("?)([^";]+)\1/i);
    if (basicMatch?.[2]) {
        return basicMatch[2].trim() || fallback;
    }

    return fallback;
};

const sanitizeDownloadFilenamePart = (value, fallback = 'workspace') => {
    const text = String(value || '').trim();
    if (!text) return fallback;
    const cleaned = text
        .replace(/[<>:"/\\|?*]+/g, ' ')
        .replace(/\s+/g, '_')
        .replace(/^[._]+/, '')
        .replace(/[._]+$/, '');
    return cleaned || fallback;
};

// ============================================================================
// Project API
// ============================================================================

/**
 * Create a new project
 */
export const createProject = async (data) => {
    const response = await api.post('/projects/', data);
    return response.data;
};

/**
 * Get all projects
 */
export const getProjects = async () => {
    const response = await api.get('/projects/');
    return response.data;
};

/**
 * Get a project by ID
 */
export const getProject = async (projectId) => {
    const response = await api.get(`/projects/${projectId}`);
    return response.data;
};

/**
 * Get aggregated dashboard data for a project detail page
 */
export const getProjectDashboard = async (projectId) => {
    const response = await api.get(`/projects/${projectId}/dashboard`);
    return response.data;
};

/**
 * Update a project
 */
export const updateProject = async (projectId, data) => {
    const response = await api.put(`/projects/${projectId}`, data);
    return response.data;
};

/**
 * Delete a project
 */
export const deleteProject = async (projectId) => {
    await api.delete(`/projects/${projectId}`);
};

// ============================================================================
// Schema Upload API
// ============================================================================

/**
 * Upload a data dictionary file
 */
export const uploadDataDictionary = async (projectId, sourceSystem, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
        `/schema/upload/${projectId}/${sourceSystem}`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );
    return response.data;
};

/**
 * Get all files for a project
 */
export const getProjectFiles = async (projectId, sourceSystem = null) => {
    const params = sourceSystem ? { source_system: sourceSystem } : {};
    const response = await api.get(`/schema/files/${projectId}`, { params });
    return response.data;
};

/**
 * Get detailed file information
 */
export const getFileDetail = async (projectId, sourceSystem, fileId) => {
    const response = await api.get(`/schema/files/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

/**
 * Get table review data for an uploaded file
 */
export const getTableReview = async (projectId, sourceSystem, fileId) => {
    const response = await api.get(`/schema/table-review/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

/**
 * Save one table review decision
 */
export const saveTableReviewDecision = async (decisionData) => {
    const response = await api.post('/schema/table-review/decision', decisionData);
    return response.data;
};

/**
 * Apply one decision to all table review items in a file
 */
export const saveTableReviewBulkDecision = async (decisionData) => {
    const response = await api.post('/schema/table-review/bulk', decisionData);
    return response.data;
};

/**
 * Add a manual table review item for a file
 */
export const addTableReviewItem = async (requestData) => {
    const response = await api.post('/schema/table-review/add', requestData);
    return response.data;
};

/**
 * Download the latest stored version of a data dictionary file
 */
export const downloadDataDictionary = async (projectId, sourceSystem, fileId) => {
    const response = await api.get(`/schema/download/${projectId}/${sourceSystem}/${fileId}`, {
        responseType: 'blob',
        timeout: 0,
    });

    const disposition = response.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);

    return {
        blob: response.data,
        filename: filenameMatch?.[1] || null,
    };
};

/**
 * Delete an uploaded file
 */
export const deleteUploadedFile = async (projectId, sourceSystem, fileId) => {
    await api.delete(`/schema/files/${projectId}/${sourceSystem}/${fileId}`);
};

/**
 * Get all tables for a source system
 */
export const getTablesBySource = async (projectId, sourceSystem) => {
    const response = await api.get(`/schema/tables/${projectId}/${sourceSystem}`);
    return response.data;
};

/**
 * Get all columns for a source system
 */
export const getColumnsBySource = async (projectId, sourceSystem, tableName = null) => {
    const params = tableName ? { table_name: tableName } : {};
    const response = await api.get(`/schema/columns/${projectId}/${sourceSystem}`, { params });
    return response.data;
};

// ============================================================================
// Enrichment API
// ============================================================================

/**
 * Get enrichment status for a file
 */
export const getEnrichmentStatus = async (projectId, sourceSystem, fileId) => {
    const response = await api.get(`/enrichment/status/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

/**
 * Get enrichment status for multiple files
 */
export const getEnrichmentStatusBatch = async (projectId, items) => {
    const response = await api.post(`/enrichment/status-batch/${projectId}`, items);
    return response.data;
};

/**
 * Enrich table and column descriptions for a file
 */
export const enrichFileDescriptions = async (projectId, sourceSystem, fileId) => {
    const response = await api.post(`/enrichment/enrich/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

/**
 * Get enrichment progress
 */
export const getEnrichmentProgress = async (projectId, sourceSystem, fileId) => {
    const response = await api.get(`/enrichment/progress/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

/**
 * Get enrichment progress for multiple files
 */
export const getEnrichmentProgressBatch = async (projectId, items) => {
    const response = await api.post(`/enrichment/progress-batch/${projectId}`, items);
    return response.data;
};

// ============================================================================
// Pipeline API
// ============================================================================

/**
 * Start a background pipeline job for a project
 */
export const startPipelineJob = async (projectId, options = {}) => {
    const response = await api.post(`/pipeline/start/${projectId}`, {
        include_enrichment: options.includeEnrichment ?? true,
        include_classification: options.includeClassification ?? true,
        include_indexing: options.includeIndexing ?? true,
        include_mapping: options.includeMapping ?? true,
        force_enrichment: options.forceEnrichment ?? false,
        force_classification: options.forceClassification ?? false,
        force_indexing: options.forceIndexing ?? false,
        force_mapping: options.forceMapping ?? false,
        source_systems: options.sourceSystems,
    });
    return response.data.job || response.data;
};

/**
 * Get pipeline job list for a project
 */
export const listPipelineJobs = async (projectId) => {
    const response = await api.get(`/pipeline/jobs/${projectId}`);
    return (response.data || []).map((entry) => entry.job || entry);
};

/**
 * Get pipeline job status for a single job
 */
export const getPipelineJobStatus = async (projectId, jobId) => {
    const response = await api.get(`/pipeline/status/${projectId}/${jobId}`);
    return response.data.job || response.data;
};

/**
 * Resume a pipeline job from checkpoint
 */
export const resumePipelineJob = async (projectId, jobId) => {
    const response = await api.post(`/pipeline/resume/${projectId}/${jobId}`);
    return response.data.job || response.data;
};

/**
 * Cancel a running pipeline job
 */
export const cancelPipelineJob = async (projectId, jobId) => {
    const response = await api.post(`/pipeline/cancel/${projectId}/${jobId}`);
    return response.data.job || response.data;
};

/**
 * Get generated column descriptions for review
 */
export const getDescriptionReview = async (projectId, sourceSystem, fileId, options = {}) => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page);
    if (options.pageSize) params.append('page_size', options.pageSize);
    if (options.statusFilter) params.append('status_filter', options.statusFilter);
    if (options.tableFilter) params.append('table_filter', options.tableFilter);
    if (options.columnFilter) params.append('column_filter', options.columnFilter);
    if (options.search) params.append('search', options.search);
    const queryString = params.toString();
    const url = `/enrichment/review/${projectId}/${sourceSystem}/${fileId}${queryString ? '?' + queryString : ''}`;
    const response = await api.get(url);
    return response.data;
};

/**
 * Save a review decision for a generated description
 */
export const saveDescriptionReviewDecision = async (decisionData) => {
    const response = await api.post('/enrichment/review/decision', decisionData);
    return response.data;
};

// ============================================================================
// Theme Classification API
// ============================================================================

export const getThemeClassificationStatusBatch = async (projectId, items) => {
    const response = await api.post(`/theme-classification/status-batch/${projectId}`, items);
    return response.data;
};

export const getThemeClassificationProgressBatch = async (projectId, items) => {
    const response = await api.post(`/theme-classification/progress-batch/${projectId}`, items);
    return response.data;
};

export const classifyFileThemes = async (projectId, sourceSystem, fileId) => {
    const response = await api.post(`/theme-classification/classify/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

export const getThemeReview = async (projectId, sourceSystem, fileId, options = {}) => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page);
    if (options.pageSize) params.append('page_size', options.pageSize);
    if (options.statusFilter) params.append('status_filter', options.statusFilter);
    if (options.tableFilter) params.append('table_filter', options.tableFilter);
    if (options.themeFilter) params.append('theme_filter', options.themeFilter);
    if (options.search) params.append('search', options.search);
    const queryString = params.toString();
    const url = `/theme-classification/review/${projectId}/${sourceSystem}/${fileId}${queryString ? '?' + queryString : ''}`;
    const response = await api.get(url);
    return response.data;
};

export const saveThemeReviewDecision = async (decisionData) => {
    const response = await api.post('/theme-classification/review/decision', decisionData);
    return response.data;
};

// ============================================================================
// Indexing API
// ============================================================================

/**
 * Index a Catalyst file to Qdrant
 */
export const indexCatalystFile = async (projectId, sourceSystem, fileId) => {
    const response = await api.post(`/indexing/index/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

/**
 * Get indexing status for a file
 */
export const getIndexingStatus = async (projectId, sourceSystem, fileId) => {
    const response = await api.get(`/indexing/status/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

/**
 * Get indexing status for multiple files
 */
export const getIndexingStatusBatch = async (projectId, items) => {
    const response = await api.post(`/indexing/status-batch/${projectId}`, items);
    return response.data;
};

/**
 * Get indexing progress for a file
 */
export const getIndexingProgress = async (projectId, sourceSystem, fileId) => {
    const response = await api.get(`/indexing/progress/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

/**
 * Get indexing progress for multiple files
 */
export const getIndexingProgressBatch = async (projectId, items) => {
    const response = await api.post(`/indexing/progress-batch/${projectId}`, items);
    return response.data;
};

/**
 * Search for similar Catalyst columns
 */
export const searchSimilarColumns = async (searchParams) => {
    const response = await api.post('/indexing/search', searchParams);
    return response.data;
};

/**
 * Get indexing search config
 */
export const getIndexingConfig = async () => {
    const response = await api.get('/indexing/config');
    return response.data;
};

/**
 * Reset indexing status for a file (to allow re-indexing)
 */
export const resetIndexingStatus = async (projectId, sourceSystem, fileId) => {
    const response = await api.post(`/indexing/reset/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

// ============================================================================
// Matching API
// ============================================================================

/**
 * Find Catalyst match for a single legacy column (3-stage matching)
 */
export const findColumnMatch = async (matchRequest) => {
    const response = await api.post('/matching/find', matchRequest);
    return response.data;
};

/**
 * Batch find Catalyst matches for multiple legacy columns
 */
export const batchFindMatches = async (batchRequest) => {
    const response = await api.post('/matching/batch', batchRequest);
    return response.data;
};

/**
 * Get matching configuration (thresholds)
 */
export const getMatchingConfig = async () => {
    const response = await api.get('/matching/config');
    return response.data;
};

// ============================================================================
// Schema Mapping API
// ============================================================================

/**
 * Process a legacy file for mapping (matching + LLM validation)
 */
export const processLegacyFile = async (projectId, sourceSystem, fileId, options = {}) => {
    const response = await api.post(`/mapping/process/${projectId}/${sourceSystem}/${fileId}`, {
        layer_filter: options.layerFilter || null,
        validate_with_llm: options.validateWithLlm !== false,
    }, {
        timeout: 0,
    });
    return response.data;
};

/**
 * Get processing status for a file
 */
export const getMappingProcessingStatus = async (projectId, sourceSystem, fileId) => {
    const response = await api.get(`/mapping/status/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

/**
 * Get mapping results for a file (organized by table)
 */
export const getMappingResults = async (projectId, sourceSystem, fileId) => {
    const response = await api.get(`/mapping/results/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

/**
 * Build a streamed CSV export URL for schema mapping results.
 */
export const buildMappingExportCsvUrl = (projectId, sourceSystem, fileId, options = {}) => {
    const params = new URLSearchParams();
    if (options.tableFilter) params.append('table_filter', options.tableFilter);
    if (options.columnFilter) params.append('column_filter', options.columnFilter);
    if (options.statusFilter) params.append('status_filter', options.statusFilter);
    if (options.matchStageFilter) params.append('match_stage_filter', options.matchStageFilter);

    const queryString = params.toString();
    return `${getApiBaseUrl()}/mapping/export/${projectId}/${sourceSystem}/${fileId}${queryString ? '?' + queryString : ''}`;
};

/**
 * Get mapping summary for a file
 */
export const getMappingSummary = async (projectId, sourceSystem, fileId) => {
    const response = await api.get(`/mapping/summary/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

/**
 * Get mapping summary for multiple files
 */
export const getMappingSummaryBatch = async (projectId, items) => {
    const response = await api.post(`/mapping/summary-batch/${projectId}`, { items });
    return response.data;
};

/**
 * Get diagnostics selector catalog for a mapped file
 */
export const getMappingDiagnosticsCatalog = async (projectId, sourceSystem, fileId, options = {}) => {
    const params = {};
    if (options.includeTargets !== undefined) {
        params.include_targets = options.includeTargets;
    }
    const response = await api.get(`/mapping/diagnostics/catalog/${projectId}/${sourceSystem}/${fileId}`, { params });
    return response.data;
};

export const startMappingDiagnosticLiveTrace = async (
    projectId,
    sourceSystem,
    fileId,
    columnKey,
    targetLayer,
    targetTable,
    targetColumn,
) => {
    const response = await api.post(`/mapping/diagnostics/live-trace/start/${projectId}/${sourceSystem}/${fileId}`, null, {
        params: {
            column_key: columnKey,
            target_layer: targetLayer,
            target_table: targetTable,
            target_column: targetColumn,
        },
    });
    return response.data;
};

export const getMappingDiagnosticLiveTraceProgress = async (
    projectId,
    sourceSystem,
    fileId,
    columnKey,
    targetLayer,
    targetTable,
    targetColumn,
    runId,
) => {
    const response = await api.get(`/mapping/diagnostics/live-trace/progress/${projectId}/${sourceSystem}/${fileId}`, {
        params: {
            column_key: columnKey,
            target_layer: targetLayer,
            target_table: targetTable,
            target_column: targetColumn,
            run_id: runId,
        },
    });
    return response.data;
};

/**
 * Save a user decision (accept/reject) for a column mapping
 */
export const saveMappingDecision = async (decisionData) => {
    const response = await api.post('/mapping/decision', decisionData);
    return response.data;
};

/**
 * Get all decisions for a file
 */
export const getMappingDecisions = async (projectId, sourceSystem, fileId, statusFilter = null) => {
    let url = `/mapping/decisions/${projectId}/${sourceSystem}/${fileId}`;
    if (statusFilter) {
        url += `?status_filter=${statusFilter}`;
    }
    const response = await api.get(url);
    return response.data;
};

/**
 * Delete mapping results for a file (to allow re-processing)
 */
export const deleteMappings = async (projectId, sourceSystem, fileId) => {
    const response = await api.delete(`/mapping/mappings/${projectId}/${sourceSystem}/${fileId}`);
    return response.data;
};

export const getSchemaMappingPreferences = async (options = {}) => {
    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.search) params.append('search', options.search);
    if (options.layer) params.append('layer', options.layer);
    if (options.limit) params.append('limit', options.limit);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/mapping/preferences${suffix}`);
    return response.data;
};

export const saveSchemaMappingPreference = async (data) => {
    const response = await api.post('/mapping/preferences/decision', data);
    return response.data;
};

export const deleteSchemaMappingPreference = async (preferenceId) => {
    const response = await api.delete(`/mapping/preferences/${preferenceId}`);
    return response.data;
};

export const downloadSchemaMappingPreferences = async () => {
    const response = await api.get('/mapping/preferences/download', {
        responseType: 'blob',
        timeout: 0,
    });
    const disposition = response.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
    return {
        blob: response.data,
        filename: filenameMatch?.[1] || null,
    };
};

export const uploadSchemaMappingPreferences = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/mapping/preferences/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 0,
    });
    return response.data;
};

/**
 * Get paginated mapping results with all matches per column
 */
export const getMappingResultsPaginated = async (projectId, sourceSystem, fileId, options = {}) => {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page);
    if (options.pageSize) params.append('page_size', options.pageSize);
    if (options.tableFilter) params.append('table_filter', options.tableFilter);
    if (options.columnFilter) params.append('column_filter', options.columnFilter);
    if (options.statusFilter) params.append('status_filter', options.statusFilter);
    if (options.matchStageFilter) params.append('match_stage_filter', options.matchStageFilter);
    if (options.hasMatches !== undefined && options.hasMatches !== null) params.append('has_matches', options.hasMatches);

    const queryString = params.toString();
    const url = `/mapping/results-paginated/${projectId}/${sourceSystem}/${fileId}${queryString ? '?' + queryString : ''}`;
    const response = await api.get(url);
    return response.data;
};

/**
 * Get full match payloads for selected mapping rows only
 */
export const getMappingResultsDetails = async (projectId, sourceSystem, fileId, columnKeys = []) => {
    const response = await api.post(`/mapping/results-details/${projectId}/${sourceSystem}/${fileId}`, {
        column_keys: columnKeys,
    });
    return response.data;
};

// ============================================================================
// Configuration API (Global Configuration)
// ============================================================================

/**
 * Get full configuration (themes, common fields, PII fields)
 */
export const getGlobalConfig = async () => {
    const response = await api.get('/config/');
    return response.data;
};

/**
 * Get themes for a specific system (legacy or catalyst)
 */
export const getThemesBySystem = async (system) => {
    const response = await api.get(`/config/themes/${system}`);
    return response.data;
};

/**
 * Get global themes for a specific system
 */
export const getGlobalThemesBySystem = async (system) => {
    const response = await api.get(`/config/themes/global/${system}`);
    return response.data;
};

/**
 * Get a specific theme
 */
export const getTheme = async (system, themeName) => {
    const response = await api.get(`/config/themes/${system}/${themeName}`);
    return response.data;
};

/**
 * Create a new theme
 */
export const createTheme = async (system, themeName, themeData) => {
    const response = await api.post(`/config/themes/${system}/${themeName}`, themeData);
    return response.data;
};

/**
 * Update an existing theme
 */
export const updateTheme = async (system, themeName, themeData) => {
    const response = await api.put(`/config/themes/${system}/${themeName}`, themeData);
    return response.data;
};

/**
 * Delete a theme
 */
export const deleteTheme = async (system, themeName) => {
    const response = await api.delete(`/config/themes/${system}/${themeName}`);
    return response.data;
};

/**
 * Create a sub-theme
 */
export const createSubTheme = async (system, themeName, subThemeName, keywords = []) => {
    const response = await api.post(
        `/config/themes/${system}/${themeName}/sub-themes/${subThemeName}`,
        keywords
    );
    return response.data;
};

/**
 * Update a sub-theme
 */
export const updateSubTheme = async (system, themeName, subThemeName, keywords) => {
    const response = await api.put(
        `/config/themes/${system}/${themeName}/sub-themes/${subThemeName}`,
        keywords
    );
    return response.data;
};

/**
 * Delete a sub-theme
 */
export const deleteSubTheme = async (system, themeName, subThemeName) => {
    const response = await api.delete(
        `/config/themes/${system}/${themeName}/sub-themes/${subThemeName}`
    );
    return response.data;
};

/**
 * Get common fields configuration
 */
export const getCommonFields = async () => {
    const response = await api.get('/config/common-fields');
    return response.data;
};

/**
 * Update common fields configuration
 */
export const updateCommonFields = async (fields) => {
    const response = await api.put('/config/common-fields', fields);
    return response.data;
};

/**
 * Get PII fields configuration
 */
export const getPiiFields = async () => {
    const response = await api.get('/config/pii-fields');
    return response.data;
};

/**
 * Update PII fields configuration
 */
export const updatePiiFields = async (fields) => {
    const response = await api.put('/config/pii-fields', fields);
    return response.data;
};

/**
 * Get table exclusion configuration
 */
export const getTableExclusions = async () => {
    const response = await api.get('/config/table-exclusions');
    return response.data;
};

/**
 * Update table exclusion configuration
 */
export const updateTableExclusions = async (fields) => {
    const response = await api.put('/config/table-exclusions', fields);
    return response.data;
};

/**
 * Get deduplication configuration
 */
export const getDeduplication = async () => {
    const response = await api.get('/config/deduplication');
    return response.data;
};

/**
 * Update deduplication configuration
 */
export const updateDeduplication = async (settings) => {
    const response = await api.put('/config/deduplication', settings);
    return response.data;
};

/**
 * Get schema mapper priority table configuration
 */
export const getSchemaMapperPriorities = async () => {
    const response = await api.get('/config/schema-mapper-priorities');
    return response.data;
};

/**
 * Get report matching configuration
 */
export const getReportMatchingSettings = async () => {
    const response = await api.get('/config/report-matching');
    return response.data;
};

/**
 * Update report matching configuration
 */
export const updateReportMatchingSettings = async (settings) => {
    const response = await api.put('/config/report-matching', settings);
    return response.data;
};

/**
 * Update schema mapper priority table configuration
 */
export const updateSchemaMapperPriorities = async (settings) => {
    const response = await api.put('/config/schema-mapper-priorities', settings);
    return response.data;
};

/**
 * Import schema mapper priority levels from an Excel workbook.
 */
export const importSchemaMapperPriorities = async (file, sortOrder = 'desc') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sort_order', sortOrder);
    const response = await api.post('/config/schema-mapper-priorities/import', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

// ============================================================================
// Shared Files API
// ============================================================================

/**
 * Upload one or more shared files
 */
export const uploadSharedFiles = async (files) => {
    const formData = new FormData();
    files.forEach((file) => {
        formData.append('files', file);
    });
    const response = await api.post('/shared-files/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

/**
 * List shared files
 */
export const getSharedFiles = async () => {
    const response = await api.get('/shared-files');
    return response.data;
};

/**
 * List shared snippets
 */
export const getSharedSnippets = async () => {
    const response = await api.get('/shared-files/snippets');
    return response.data;
};

/**
 * Create a shared snippet
 */
export const createSharedSnippet = async (payload) => {
    const response = await api.post('/shared-files/snippets', payload);
    return response.data;
};

/**
 * Delete a shared file
 */
export const deleteSharedFile = async (fileId) => {
    await api.delete(`/shared-files/${fileId}`);
};

/**
 * Delete a shared snippet
 */
export const deleteSharedSnippet = async (snippetId) => {
    await api.delete(`/shared-files/snippets/${snippetId}`);
};

/**
 * Build a download URL for a shared file
 */
export const getSharedFileDownloadUrl = (fileId) => {
    const base = getApiBaseUrl();
    const normalized = base.endsWith('/') ? base.slice(0, -1) : base;
    return `${normalized}/shared-files/${fileId}/download`;
};

// ============================================================================
// Lineage API (Report Matching - Lineage Files)
// ============================================================================

/**
 * Upload a lineage Excel file
 */
export const uploadLineageFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/lineage/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
};

/**
 * Get list of uploaded lineage files
 */
export const getLineageFiles = async () => {
    const response = await api.get('/lineage/files');
    return response.data;
};

/**
 * Get details of a specific lineage file
 */
export const getLineageFile = async (fileId) => {
    const response = await api.get(`/lineage/${fileId}`);
    return response.data;
};

/**
 * Get lineage indexing diagnostics for catalyst rows
 */
export const getLineageIndexDiagnostics = async (fileId) => {
    const response = await api.get(`/lineage/${fileId}/index-diagnostics`);
    return response.data;
};

/**
 * Get reports from a lineage file
 */
export const getLineageReports = async (fileId, system, includeColumns = false) => {
    const response = await api.get(`/lineage/${fileId}/reports/${system}?include_columns=${includeColumns}`);
    return response.data;
};

/**
 * Get a specific report with columns from a lineage file
 */
export const getLineageReport = async (fileId, system, reportName) => {
    const response = await api.get(`/lineage/${fileId}/reports/${system}/${encodeURIComponent(reportName)}`);
    return response.data;
};

export const updateLineageReportThemes = async (fileId, system, reportName, data) => {
    const response = await api.put(`/lineage/${fileId}/reports/${system}/${encodeURIComponent(reportName)}/themes`, data);
    return response.data;
};

/**
 * Classify reports into themes
 */
export const classifyLineageReports = async (fileId) => {
    const response = await api.post(`/lineage/${fileId}/classify`);
    return response.data;
};

/**
 * Get lineage file statistics
 */
export const getLineageStats = async (fileId) => {
    const response = await api.get(`/lineage/${fileId}/stats`);
    return response.data;
};

/**
 * Delete a lineage file
 */
export const deleteLineageFile = async (fileId) => {
    const response = await api.delete(`/lineage/${fileId}`);
    return response.data;
};

// ============================================================================
// Report Matching API
// ============================================================================

/**
 * Find matching catalyst reports for a legacy report
 */
export const findMatchingReports = async (request) => {
    const response = await api.post('/report-matching/find-reports', request);
    return response.data;
};

/**
 * Analyze gaps between legacy and catalyst reports
 */
export const analyzeGaps = async (workspaceId, legacyReportName, catalystReportName, options = {}) => {
    const params = new URLSearchParams({
        legacy_report_name: legacyReportName,
        catalyst_report_name: catalystReportName,
        use_llm: true,
    });
    if (options.projectId) params.append('project_id', options.projectId);
    if (options.ignoreCommon !== undefined) params.append('ignore_common', options.ignoreCommon);
    if (options.ignorePii !== undefined) params.append('ignore_pii', options.ignorePii);
    if (options.matchOnlyLayout !== undefined) params.append('match_only_layout', options.matchOnlyLayout);
    const response = await api.get(`/lineage/workspaces/${workspaceId}/gap-analysis?${params.toString()}`, {
        timeout: 0,
    });
    return response.data;
};

/**
 * Get matching configuration
 */
export const getReportMatchingConfig = async () => {
    const response = await api.get('/report-matching/config');
    return response.data;
};

// ============================================================================
// Workspace API (Report Matching Workspaces)
// ============================================================================

/**
 * Create a new workspace
 */
export const createWorkspace = async (workspaceData) => {
    const response = await api.post('/lineage/workspaces', workspaceData);
    return response.data;
};

/**
 * Get list of workspaces
 */
export const getWorkspaces = async () => {
    const response = await api.get('/lineage/workspaces');
    return response.data;
};

/**
 * Get workspace details
 */
export const getWorkspace = async (workspaceId) => {
    const response = await api.get(`/lineage/workspaces/${workspaceId}`);
    return response.data;
};

/**
 * Download the cleaned lineage workbook for a workspace
 */
export const downloadWorkspaceLineage = async (workspaceId, workspaceName = '') => {
    const response = await api.get(`/lineage/workspaces/${workspaceId}/download-lineage`, {
        responseType: 'blob',
    });
    return {
        blob: response.data,
        filename: parseDownloadFilename(
            response.headers?.['content-disposition'],
            `report_lineage_${sanitizeDownloadFilenamePart(workspaceName, `workspace_${workspaceId}`)}.xlsx`
        ),
    };
};

/**
 * Update workspace
 */
export const updateWorkspace = async (workspaceId, data) => {
    const response = await api.put(`/lineage/workspaces/${workspaceId}`, data);
    return response.data;
};

/**
 * Delete workspace
 */
export const deleteWorkspace = async (workspaceId) => {
    const response = await api.delete(`/lineage/workspaces/${workspaceId}`);
    return response.data;
};

/**
 * Upload lineage file to workspace
 */
export const uploadToWorkspace = async (workspaceId, file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = {};
    if (options.incremental) {
        params.incremental = true;
    }
    const response = await api.post(`/lineage/workspaces/${workspaceId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params,
    });
    return response.data;
};

/**
 * Classify reports in workspace
 */
export const classifyWorkspace = async (workspaceId) => {
    const response = await api.post(`/lineage/workspaces/${workspaceId}/classify`);
    return response.data;
};

export const getWorkspaceClassificationStatus = async (workspaceId) => {
    const response = await api.get(`/lineage/workspaces/${workspaceId}/classify/status`);
    return response.data;
};

/**
 * Save matching result to workspace
 */
export const saveMatchingResult = async (workspaceId, data) => {
    const response = await api.post(`/lineage/workspaces/${workspaceId}/matchings`, data);
    return response.data;
};

/**
 * Get saved matchings in workspace
 */
export const getWorkspaceMatchings = async (workspaceId) => {
    const response = await api.get(`/lineage/workspaces/${workspaceId}/matchings`);
    return response.data;
};

/**
 * Get a specific saved matching
 */
export const getSavedMatching = async (workspaceId, reportName) => {
    const response = await api.get(`/lineage/workspaces/${workspaceId}/matchings/${encodeURIComponent(reportName)}`);
    return response.data;
};

/**
 * Compare one legacy report and one Catalyst report in the same workspace.
 */
export const compareWorkspaceReports = async (workspaceId, legacyReportName, catalystReportName, options = {}) => {
    const response = await api.get(`/lineage/workspaces/${workspaceId}/report-compare`, {
        timeout: 180000,
        params: {
            legacy_report_name: legacyReportName,
            catalyst_report_name: catalystReportName,
            top_n: options.topN,
            custom_top_n: options.customTopN,
            standard_other_theme_top_n: options.standardOtherThemeTopN,
            final_match_threshold: options.finalMatchThreshold,
            same_theme_only: options.sameThemeOnly,
            global_theme_only: options.globalThemeOnly,
            ignore_common: options.ignoreCommon,
            ignore_pii: options.ignorePii,
            use_llm: true,
            include_custom: options.includeCustom,
        },
    });
    return response.data;
};

/**
 * Delete a saved matching
 */
export const deleteMatchingResult = async (workspaceId, reportName) => {
    const response = await api.delete(`/lineage/workspaces/${workspaceId}/matchings/${encodeURIComponent(reportName)}`);
    return response.data;
};

/**
 * Save gap analysis to workspace
 */
export const saveGapAnalysis = async (workspaceId, data) => {
    const response = await api.post(`/lineage/workspaces/${workspaceId}/gaps`, data);
    return response.data;
};

/**
 * Generate recommendations for missing columns
 */
export const getRecommendations = async (workspaceId, request) => {
    const response = await api.post(`/lineage/workspaces/${workspaceId}/recommendations`, request);
    return response.data;
};

export const getSavedRecommendation = async (workspaceId, legacyReportName, catalystReportName) => {
    const params = new URLSearchParams({
        legacy_report_name: legacyReportName,
        catalyst_report_name: catalystReportName,
    });
    const response = await api.get(`/lineage/workspaces/${workspaceId}/recommendations-item?${params.toString()}`);
    return response.data;
};

export const deleteSavedRecommendation = async (workspaceId, legacyReportName, catalystReportName) => {
    const params = new URLSearchParams({
        legacy_report_name: legacyReportName,
        catalyst_report_name: catalystReportName,
    });
    const response = await api.delete(`/lineage/workspaces/${workspaceId}/recommendations-item?${params.toString()}`);
    return response.data;
};

export const updateSavedRecommendationDeveloperUse = async (workspaceId, data) => {
    const response = await api.patch(`/lineage/workspaces/${workspaceId}/recommendations-item/developer-use`, data);
    return response.data;
};

export const startRecommendationJob = async (workspaceId, request) => {
    const response = await api.post(`/lineage/workspaces/${workspaceId}/recommendation-jobs`, request);
    return response.data;
};

export const getRecommendationJob = async (workspaceId, jobId) => {
    const response = await api.get(`/lineage/workspaces/${workspaceId}/recommendation-jobs/${jobId}`);
    return response.data;
};

export const listRecommendationJobs = async (workspaceId, options = {}) => {
    const params = new URLSearchParams();
    if (options.legacyReportName) params.append('legacy_report_name', options.legacyReportName);
    if (options.catalystReportName) params.append('catalyst_report_name', options.catalystReportName);
    if (options.limit) params.append('limit', options.limit);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/lineage/workspaces/${workspaceId}/recommendation-jobs${suffix}`);
    return response.data;
};

/**
 * Index Catalyst columns in workspace for semantic search
 */
export const indexWorkspace = async (workspaceId) => {
    const response = await api.post(`/lineage/workspaces/${workspaceId}/index`);
    return response.data;
};

export const getWorkspaceIndexStatus = async (workspaceId) => {
    const response = await api.get(`/lineage/workspaces/${workspaceId}/index/status`);
    return response.data;
};

/**
 * Find semantically matching Catalyst reports for a Legacy report
 */
export const findSemanticMatches = async (workspaceId, legacyReportName, options = {}) => {
    const params = new URLSearchParams({
        legacy_report_name: legacyReportName,
        top_n: options.topN || 5,
        same_theme_only: options.sameThemeOnly !== false,
        global_theme_only: options.globalThemeOnly || false,
        ignore_common: options.ignoreCommon !== false,
        ignore_pii: options.ignorePII !== false,
        use_llm: true,
        include_custom: options.includeCustom ?? true
    });
    const response = await api.post(`/lineage/workspaces/${workspaceId}/match?${params.toString()}`);
    return response.data;
};

export const startReportMatchingJob = async (workspaceId, legacyReportName, options = {}) => {
    const params = new URLSearchParams({
        legacy_report_name: legacyReportName,
        top_n: options.topN || 5,
        standard_top_n: options.standardTopN || options.topN || 5,
        custom_top_n: options.customTopN || options.topN || 5,
        standard_other_theme_top_n: options.standardOtherThemeTopN || options.topN || 5,
        final_match_threshold: options.finalMatchThreshold ?? 50,
        same_theme_only: options.sameThemeOnly !== false,
        global_theme_only: options.globalThemeOnly || false,
        ignore_common: options.ignoreCommon !== false,
        ignore_pii: options.ignorePII !== false,
        use_llm: true,
        include_custom: options.includeCustom ?? true
    });
    const response = await api.post(`/lineage/workspaces/${workspaceId}/match-jobs?${params.toString()}`);
    return response.data;
};

export const getReportMatchingJob = async (workspaceId, jobId) => {
    const response = await api.get(`/lineage/workspaces/${workspaceId}/match-jobs/${jobId}`);
    return response.data;
};

export const cancelReportMatchingJob = async (workspaceId, jobId) => {
    const response = await api.post(`/lineage/workspaces/${workspaceId}/match-jobs/${jobId}/cancel`);
    return response.data;
};

export const deleteReportMatchingJob = async (workspaceId, jobId) => {
    const response = await api.delete(`/lineage/workspaces/${workspaceId}/match-jobs/${jobId}`);
    return response.data;
};

export const listReportMatchingJobs = async (workspaceId, options = {}) => {
    const params = new URLSearchParams();
    if (options.legacyReportName) params.append('legacy_report_name', options.legacyReportName);
    if (options.limit) params.append('limit', options.limit);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/lineage/workspaces/${workspaceId}/match-jobs${suffix}`);
    return response.data;
};

export const cancelRecommendationJob = async (workspaceId, jobId) => {
    const response = await api.post(`/lineage/workspaces/${workspaceId}/recommendation-jobs/${jobId}/cancel`);
    return response.data;
};

/**
 * Export gap analysis results to Excel
 */
export const exportGapAnalysis = async (data) => {
    const response = await api.post('/report-matching/export-gap-analysis', data, {
        responseType: 'blob',
        timeout: 0,
    });
    return response.data;
};

/**
 * Export full recommendations results to Excel
 */
export const exportRecommendations = async (data) => {
    const response = await api.post('/report-matching/export-recommendations', data, {
        responseType: 'blob',
        timeout: 0,
    });
    return response.data;
};

export const getReportMatchingPreferences = async (options = {}) => {
    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.search) params.append('search', options.search);
    if (options.limit) params.append('limit', options.limit);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/report-matching/preferences${suffix}`);
    return response.data;
};

export const saveReportMatchingPreference = async (data) => {
    const response = await api.post('/report-matching/preferences/decision', data);
    return response.data;
};

export const deleteReportMatchingPreference = async (preferenceId) => {
    const response = await api.delete(`/report-matching/preferences/${preferenceId}`);
    return response.data;
};

export const downloadReportMatchingPreferences = async () => {
    const response = await api.get('/report-matching/preferences/download', {
        responseType: 'blob',
        timeout: 0,
    });
    const disposition = response.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
    return {
        blob: response.data,
        filename: filenameMatch?.[1] || null,
    };
};

export const uploadReportMatchingPreferences = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/report-matching/preferences/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout: 0,
    });
    return response.data;
};

export default api;
